import { useEffect, useState } from "react";
import { Trash2, Pencil, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";

export default function AdminOpenStudio({ ctx }) {
  const { osProjects, expDrafts, setExpField, saveExperience, toggleProject, delProject, newProject, setNewProject, addProject, osBookings, delOsBooking, reload } = ctx;
  const [slots, setSlots] = useState([]);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ booking_date: "", time_slot: "", num_guests: 1, exp: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/open-studio/info").then(({ data }) => setSlots(data?.time_slots || [])).catch(() => {});
  }, []);

  const startEdit = (b) => {
    const exp = {};
    (b.experiences || []).forEach((e) => { exp[e.name] = e.qty; });
    setDraft({ booking_date: b.booking_date || "", time_slot: b.time_slot || "", num_guests: b.num_guests || 1, exp });
    setEditId(b.id);
  };
  const setExpQty = (name, delta) =>
    setDraft((d) => ({ ...d, exp: { ...d.exp, [name]: Math.max(0, (d.exp[name] || 0) + delta) } }));

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      const experiences = Object.entries(draft.exp).filter(([, q]) => q > 0).map(([name, qty]) => ({ name, qty }));
      await api.put(`/admin/open-studio/bookings/${id}`, {
        booking_date: draft.booking_date, time_slot: draft.time_slot,
        num_guests: Number(draft.num_guests), experiences,
      });
      toast.success("Reservation updated");
      setEditId(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10 space-y-8" data-testid="admin-open-studio">
      <div className="p-6 bg-white border-2 border-[#121212]">
        <h3 className="font-serif font-black text-2xl text-[#121212] mb-1">Decorating experiences</h3>
        <p className="text-sm text-stone-500 mb-4">These are the cake experiences guests choose when reserving a table. Edit names, prices, photos and visibility.</p>
        <div className="space-y-4">
          {osProjects.map((p) => {
            const d = { ...p, ...(expDrafts[p.id] || {}) };
            return (
              <div key={p.id} data-testid={`os-exp-${p.id}`} className="p-4 border-2 border-[#121212] space-y-3">
                <ImageUpload library label="photo" testid={`os-exp-img-${p.id}`} value={d.image_url || ""} onChange={(url) => setExpField(p.id, "image_url", url)} />
                <div className="space-y-2">
                  <div className="flex gap-2 flex-wrap">
                    <input data-testid={`os-exp-emoji-${p.id}`} value={d.emoji || ""} onChange={(e) => setExpField(p.id, "emoji", e.target.value)} placeholder="🎂" className="w-16 px-2 py-2 border-2 border-[#121212] text-center" />
                    <input data-testid={`os-exp-name-${p.id}`} value={d.name || ""} onChange={(e) => setExpField(p.id, "name", e.target.value)} placeholder="Experience name" className="flex-1 min-w-[160px] px-3 py-2 border-2 border-[#121212]" />
                    <div className="flex items-center border-2 border-[#121212]">
                      <span className="px-2 text-stone-500">$</span>
                      <input data-testid={`os-exp-price-${p.id}`} type="number" value={d.price ?? 0} onChange={(e) => setExpField(p.id, "price", e.target.value)} className="w-20 px-2 py-2 focus:outline-none" />
                    </div>
                  </div>
                  <textarea data-testid={`os-exp-desc-${p.id}`} rows={2} value={d.description || ""} onChange={(e) => setExpField(p.id, "description", e.target.value)} placeholder="Short one-line description" className="w-full px-3 py-2 border-2 border-[#121212] text-sm" />
                  <div className="flex gap-2 items-center flex-wrap">
                    <button data-testid={`os-exp-save-${p.id}`} onClick={() => saveExperience(p)} className="px-5 py-2 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.15em] uppercase font-bold">Save</button>
                    <button data-testid={`toggle-project-${p.id}`} onClick={() => toggleProject(p)} className={`px-3 py-2 text-xs tracking-[0.15em] uppercase font-bold border-2 border-[#121212] ${p.active ? "bg-[#C6FF33]" : "bg-stone-100"}`}>{p.active ? "Active" : "Hidden"}</button>
                    <button data-testid={`os-exp-nodyes-${p.id}`} onClick={() => setExpField(p.id, "no_dyes", !d.no_dyes)} className={`px-3 py-2 text-xs tracking-[0.12em] uppercase font-bold border-2 border-[#121212] ${d.no_dyes ? "bg-[#C6FF33]" : "bg-white text-stone-500"}`} title="Made without artificial food dyes">🌿 No dyes {d.no_dyes ? "✓" : ""}</button>
                    <button data-testid={`del-project-${p.id}`} onClick={() => delProject(p.id)} className="ml-auto p-2 text-stone-500 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 pt-5 border-t-2 border-[#121212]/10">
          <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2">Add a new experience</p>
          <div className="flex gap-2 flex-wrap">
            <input data-testid="new-project-emoji" value={newProject.emoji} onChange={(e) => setNewProject({ ...newProject, emoji: e.target.value })} placeholder="🎂" className="w-16 px-2 py-2 border-2 border-[#121212] text-center" />
            <input data-testid="new-project-input" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} placeholder="Experience name…" className="flex-1 min-w-[160px] px-4 py-2 border-2 border-[#121212] focus:outline-none" />
            <div className="flex items-center border-2 border-[#121212]"><span className="px-2 text-stone-500">$</span><input data-testid="new-project-price" type="number" value={newProject.price} onChange={(e) => setNewProject({ ...newProject, price: e.target.value })} className="w-20 px-2 py-2 focus:outline-none" /></div>
            <button data-testid="add-project-btn" onClick={addProject} className="px-6 py-2 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold">Add</button>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-serif font-black text-2xl text-[#121212] mb-4">Open Studio bookings</h3>
        <div className="space-y-2">
          {osBookings.length === 0 ? <p className="text-[#121212]/60">No Open Studio bookings yet.</p> :
            osBookings.map((b) => (
              <div key={b.id} data-testid={`os-booking-${b.id}`} className="p-4 bg-white border-2 border-[#121212]">
                <div className="flex justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-medium text-stone-900">{b.user_name} · {b.user_email}</p>
                    <p className="text-sm text-stone-500">{b.booking_date} · {b.time_slot} · {b.num_guests || 1} guest{(b.num_guests || 1) > 1 ? "s" : ""}</p>
                    {b.experiences?.length > 0 && <p className="text-xs text-stone-500">{b.experiences.map((e) => `${e.qty}× ${e.name}`).join(", ")}</p>}
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-3 py-1 text-xs tracking-[0.15em] uppercase font-bold border-2 border-[#121212] h-fit ${
                        b.status === "paid" || b.status === "confirmed" ? "bg-[#C6FF33]" :
                        b.status === "pending_payment" ? "bg-amber-100" : "bg-stone-100"
                      }`}>{b.status.replace(/_/g, " ")}</span>
                      <span className="text-xs text-[#121212]/60">{b.is_free ? "Free (member)" : `$${b.amount}`}</span>
                    </div>
                    <button data-testid={`edit-os-booking-${b.id}`} onClick={() => (editId === b.id ? setEditId(null) : startEdit(b))} title="Edit reservation" className="p-2 text-stone-500 hover:text-[#121212]">{editId === b.id ? <X size={18} /> : <Pencil size={18} />}</button>
                    <button data-testid={`delete-os-booking-${b.id}`} onClick={() => delOsBooking(b.id)} title="Delete booking" className="p-2 text-stone-400 hover:text-red-500"><Trash2 size={18} /></button>
                  </div>
                </div>

                {editId === b.id && (
                  <div data-testid={`os-edit-panel-${b.id}`} className="mt-4 pt-4 border-t-2 border-[#121212]/10 space-y-4">
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold block mb-1">Date</label>
                        <input data-testid={`os-edit-date-${b.id}`} type="date" value={draft.booking_date} onChange={(e) => setDraft({ ...draft, booking_date: e.target.value })} className="w-full px-3 py-2 border-2 border-[#121212] text-sm" />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold block mb-1">Time slot</label>
                        <select data-testid={`os-edit-slot-${b.id}`} value={draft.time_slot} onChange={(e) => setDraft({ ...draft, time_slot: e.target.value })} className="w-full px-3 py-2 border-2 border-[#121212] text-sm bg-white">
                          {!slots.includes(draft.time_slot) && draft.time_slot && <option value={draft.time_slot}>{draft.time_slot}</option>}
                          {slots.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold block mb-1">Guests</label>
                        <select data-testid={`os-edit-guests-${b.id}`} value={draft.num_guests} onChange={(e) => setDraft({ ...draft, num_guests: e.target.value })} className="w-full px-3 py-2 border-2 border-[#121212] text-sm bg-white">
                          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold block mb-2">Decorating experiences (project)</label>
                      <div className="space-y-2">
                        {osProjects.map((p) => (
                          <div key={p.id} className="flex items-center justify-between gap-3 border-2 border-[#121212]/20 px-3 py-2">
                            <span className="text-sm font-medium text-[#121212]">{p.emoji} {p.name} <span className="text-[#121212]/50">· ${p.price}</span></span>
                            <div className="flex items-center gap-2">
                              <button data-testid={`os-edit-exp-dec-${b.id}-${p.id}`} onClick={() => setExpQty(p.name, -1)} className="w-7 h-7 border-2 border-[#121212] font-bold hover:bg-[#F2E9D8]">−</button>
                              <span className="w-6 text-center font-bold text-sm">{draft.exp[p.name] || 0}</span>
                              <button data-testid={`os-edit-exp-inc-${b.id}-${p.id}`} onClick={() => setExpQty(p.name, 1)} className="w-7 h-7 border-2 border-[#121212] font-bold hover:bg-[#F2E9D8]">+</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`os-edit-save-${b.id}`} onClick={() => saveEdit(b.id)} disabled={saving} className="px-6 py-2 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.15em] uppercase font-bold">{saving ? "Saving…" : "Save changes"}</button>
                      <button data-testid={`os-edit-cancel-${b.id}`} onClick={() => setEditId(null)} className="px-6 py-2 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#F2E9D8]">Cancel</button>
                    </div>
                    <p className="text-xs text-[#121212]/50">Manual change only — this updates the reservation on record and does not charge or refund the customer.</p>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
